import React, { useEffect, useRef, useState } from 'react'
import { debounce } from 'lodash'
import { useCookies } from 'react-cookie'
import queryString from 'query-string'

import { useAppSelector } from 'redux/hooks'

import DeckService from 'services/deck.service'
import RequestService from 'services/request.service'
import ToastService from 'services/toast.service'
import CustomCardService from 'services/customCards.service'

import { CardType, defaultCard, defaultColorLabel } from 'types/deck'
import { IN_DECK } from 'types/search'
import { DecksWithCardResponseType, DeckWithCardType } from 'services/apiTypes/deck.types'
import { CustomCard } from 'services/apiTypes/customCards.types'

import PhatButton from 'components/formElements/PhatButton'
import CardDetailsOverlay from 'components/elements/Overlay/CardDetailsOverlay'
import SimpleSpinner from 'components/elements/SimpleSpinner'
import AsyncDropdown, { Option } from 'components/elements/AsyncDropdown'
import PhatDropdown from 'components/formElements/PhatDropdown'
import Icon from 'components/elements/Icon'
import QuantityInput from 'components/card/cardForm/quantityInput'
import CheckboxFancybox from 'components/formElements/CheckboxFancybox'

import { getCardDefaultCategoryFromCard } from 'utils/CategoryGenerator'
import { convertCardTypeToCustomCard, convertCustomCardsToCardType } from 'hooks/customCard'

import styles from './addToOtherDeck.module.scss'

type Props = {
  label?: string
  onAdded?: (deckName: string) => void
} & ({ card: CardType; cards?: never } | { card?: never; cards: CardType[] }) &
  (
    | { parentControlled: true; open: boolean; onClose: () => void }
    | { parentControlled?: false; open?: never; onClose?: never }
  )

const AddToOtherDeck = ({ card, cards, label, parentControlled = false, onAdded, open: parentOpen }: Props) => {
  const selectedCards = card ? [card] : cards

  const [{ tbId: userId }] = useCookies(['tbId'])
  const [maintainCardQuantities, setMaintainCardQuantities] = useState(!!cards)
  const [quantity, setQuantity] = useState(card?.qty || 1)

  const currentDeckId = useAppSelector(state => state.deck.id)

  // prettier-ignore
  const [selectedCategory, setSelectedCategory] = useState(selectedCards.length > 1 ? null : getCardDefaultCategoryFromCard(selectedCards[0]))
  const [selectedDeckId, setSelectedDeckId] = useState<number | null>(null)
  const [searchedDeckName, setSearchedDeckName] = useState('')
  const [dropdownLoading, setDropdownLoading] = useState(false)
  const [decksWithCard, setDecksWithCard] = useState<Array<DeckWithCardType>>([])
  const [addLoading, setAddLoading] = useState(false)
  const [open, setOpen] = useState(parentOpen || false)

  useEffect(() => {
    if (!parentControlled) return

    setOpen(parentOpen || false)
  }, [parentOpen, parentControlled])

  useEffect(() => {
    if (!open) setDecksWithCard([])
  }, [open])

  const fetchDecks = async (deckName?: string) => {
    const params: Record<string, any> = {}

    if (card) params.id = card.cardId
    if (deckName) params.deckName = deckName

    const requestUrl = `/api/users/${userId}/decksWithCard/?${queryString.stringify(params)}`

    RequestService.get<DecksWithCardResponseType>(requestUrl)
      .then(data => setDecksWithCard(data.decks))
      .finally(() => setDropdownLoading(false))
  }

  const debouncedFetchDecks = useRef(debounce(fetchDecks, 500)).current
  const deckSearchInputRef = useRef<any>(null)

  const handleSearchForDecks = (deckName?: string) => {
    setSearchedDeckName(deckName || '')
    setDropdownLoading(true)

    debouncedFetchDecks(deckName)
  }

  const handleAddToOtherDeck = async () => {
    const deck = decksWithCard.find(obj => obj.id === selectedDeckId)

    if (!deck) return
    if (deck.hasCard !== IN_DECK.NOT_IN_DECK) return

    setAddLoading(true)

    const cardsToAdd = [...selectedCards]

    // prettier-ignore
    const unownedCustomCards = selectedCards.filter(card => !!card.customCard && `${card.customCard?.ownerId}` !== `${userId}`)

    if (unownedCustomCards.length) {
      const copiedCustomCards = unownedCustomCards.map(card => ({
        ...convertCardTypeToCustomCard(card),
        id: undefined, // Remove any existing IDs
        frontImageUrl: null,
        backImageUrl: null,
        owner: userId,
      }))

      try {
        // Create copies of any custom cards that the user does not own
        // Users can only have custom cards they own in their decks (NOT other users') so we make copies prior to adding any to their decks
        const newCustomCards = await CustomCardService.bulkCreate(copiedCustomCards)

        ToastService.create(`${newCustomCards.length} custom card(s) created`, 'Custom Card Service', 'success')

        const newCustomCardsMap = newCustomCards.reduce(
          (acc, customCard) => ({ ...acc, [customCard.frontName]: customCard }),
          {} as Record<string, CustomCard>,
        )

        // Replace any custom cards with their copies
        for (let index = 0; index < cardsToAdd.length; index++) {
          const card = cardsToAdd[index]
          const cardName = card.front?.name || card.name

          if (!card.customCard || !newCustomCardsMap[cardName]) continue

          const [replacementCard] = convertCustomCardsToCardType(newCustomCardsMap[cardName])

          cardsToAdd[index] = replacementCard
        }
      } catch (e) {
        return ToastService.create(
          'Unable to create custom cards. No card(s) were added to decks.',
          'Custom Card Service',
          'error',
        )
      }
    }

    return DeckService.add(
      deck.id,
      cardsToAdd.map((card, index) => {
        const defaultCategory = getCardDefaultCategoryFromCard(card)
        const currentCategory = card.categories[0] || defaultCategory

        let categories = [defaultCategory]

        if (selectedCategory === 'Default') categories = [defaultCategory]
        else if (selectedCategory === 'Current') categories = [currentCategory]
        else if (selectedCategory) categories = [selectedCategory]

        return {
          ...card,
          qty: (maintainCardQuantities ? card.qty : quantity) || 1,
          colorLabel: defaultColorLabel,
          modifier: card.modifier || card.options[0],
          categories: categories,
        }
      }),
    )
      .then(() => {
        if (!!card) ToastService.create(`${card?.name} added to ${deck.name}`, 'Deck service', 'success')
        if (selectedCards.length > 1)
          ToastService.create(`${selectedCards.length} cards added to ${deck.name}`, 'Deck service', 'success')

        setOpen(false)
        setSelectedCategory(null)
        setDecksWithCard([])
        setSelectedDeckId(null)
        setSearchedDeckName('')
      })
      .catch(err => RequestService.createToast(err))
      .finally(() => {
        setAddLoading(false)

        if (onAdded) onAdded(deck.name)
      })
  }

  const isAddingMultipleCards = !card
  const defaultCategory = getCardDefaultCategoryFromCard(selectedCards[0])
  const currentlySelectedDeck = decksWithCard.find(o => `${o.id}` === `${selectedDeckId}`)

  const unownedCustomCards = selectedCards.filter(
    card => !!card?.customCard && `${card?.customCard?.ownerId}` !== `${userId}`,
  )

  const options: Option[] = decksWithCard.map(deck => {
    let label = deck.name

    if (`${deck.id}` === `${currentDeckId}`) `${deck.name} (Currently viewed deck)`
    else if (deck.hasCard === IN_DECK.NAME_IN_DECK) label = `${deck.name} (Different Edition)`
    else if (deck.hasCard === IN_DECK.EXACT_IN_DECK) label = `${deck.name} (In Deck)`

    return {
      label,
      id: `${deck.id}`,
      value: deck.id,
      disabled: deck.id === currentDeckId,
    }
  })

  return (
    <>
      {!parentControlled && (
        <PhatButton onClick={() => setOpen(true)}>
          <Icon name="plus" icon="folder" /> {label ? label : 'Add to other deck'}
        </PhatButton>
      )}

      <CardDetailsOverlay
        rightControls={null}
        tabs={[
          {
            id: 'add-to-other-deck',
            label: 'Add to other deck',
            body: (
              <div className={styles.container}>
                {card?.name && <h3>Add {card?.name} to other deck</h3>}
                {selectedCards.length > 1 && <h3>Add {selectedCards.length} card(s) to other deck</h3>}

                {!!unownedCustomCards.length && (
                  <p className={styles.customCardBlurb}>
                    You've seleted to add at least one custom card you do not own to add to a deck. Doing so will create
                    a copy of the Custom Card under your account; we will then add the copy to your deck.
                  </p>
                )}

                <AsyncDropdown
                  ref={deckSearchInputRef}
                  placeholder="Select a deck..."
                  loading={dropdownLoading}
                  options={options}
                  onChange={id => {
                    const selectedDeck = options.find(o => `${o.id}` === `${id}`)

                    if (!selectedDeck) return

                    setSelectedDeckId(id)

                    // @ts-ignore
                    setSearchedDeckName(selectedDeck.label || '')
                  }}
                  onSearchChange={handleSearchForDecks}
                  onFocus={() => {
                    deckSearchInputRef?.current?.select()

                    if (!options.length) handleSearchForDecks()
                  }}
                  value={searchedDeckName}
                  triggerClassName={styles.deckSearchDropdown}
                />

                <PhatDropdown
                  options={[
                    {
                      label: isAddingMultipleCards ? 'Default Categories (Personal, Community, type)' : defaultCategory,
                      id: isAddingMultipleCards ? 'Default' : defaultCategory,
                      onClick: () => setSelectedCategory(isAddingMultipleCards ? 'Default' : defaultCategory),
                    },
                    { type: 'spacer' },
                    {
                      label: card ? card.categories[0] : 'Use current category names',
                      id: 'Current',
                      onClick: () => setSelectedCategory('Current'),
                    },
                    { type: 'spacer' },
                    { label: 'Sideboard', id: 'Sideboard', onClick: () => setSelectedCategory('Sideboard') },
                    { label: 'Maybeboard', id: 'Maybeboard', onClick: () => setSelectedCategory('Maybeboard') },
                  ]}
                  value={selectedCategory}
                  disabled={!currentlySelectedDeck || currentlySelectedDeck.hasCard !== IN_DECK.NOT_IN_DECK}
                  className={styles.categoryDropdown}
                />

                <QuantityInput
                  label="Quantity override"
                  quantity={quantity}
                  onQuantityUpdate={setQuantity}
                  className={styles.quantity}
                  disabled={maintainCardQuantities}
                />

                {/* Only show this option when dealing with multiple cards at once, otherwise the above quantity overrides will suffice */}
                {!!cards && (
                  <CheckboxFancybox
                    header="Maintain quantities"
                    checked={maintainCardQuantities}
                    onClick={() => setMaintainCardQuantities(!maintainCardQuantities)}
                    description="Use quantities from existing card record(s)"
                  />
                )}

                <div className={styles.controls}>
                  <button
                    disabled={
                      !currentlySelectedDeck || currentlySelectedDeck.hasCard !== IN_DECK.NOT_IN_DECK || addLoading
                    }
                    onClick={handleAddToOtherDeck}>
                    {!addLoading && 'Add Card(s)'}
                    {addLoading && (
                      <>
                        <SimpleSpinner size="medXSmall" /> Adding card(s)...
                      </>
                    )}
                  </button>
                </div>
              </div>
            ),
          },
        ]}
        onTabChange={() => null}
        activeTabId="add-to-other-deck"
        card={card || defaultCard}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  )
}

export default AddToOtherDeck
